/**
 * Task Management API Service
 * Handles all task-related API calls for member task tracking
 */

import axios from 'axios';
import { config } from '@/src/config';

const API_URL = config.weaveinApi.baseUrl;

// Type definitions
export type TaskType = 'task' | 'note' | 'assessment' | 'follow_up' | 'documentation' | 'communication' | 'care_coordination';
export type TaskStatus = 'pending_review' | 'not_started' | 'in_progress' | 'waiting_on_member' | 'waiting_on_external' | 'completed' | 'cancelled' | 'overdue';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface Subtask {
  id?: number;
  parent_task_id?: number;
  title: string;
  description?: string;
  assigned_to?: number;
  due_date?: string;
  sort_order: number;
  is_completed: boolean;
  completed_at?: string;
  completed_by?: number;
  created_at?: string;
  created_by?: number;
}

/** A colleague in the caller's org who can own a task. */
export interface AssignableUser {
  id: number;
  name: string;
  email?: string;
  org_role?: string;
}

export interface Task {
  id?: number;
  /** Optional — staff / org-level "my day" tasks have no member. */
  member_id?: number | null;
  cbo_id?: number;
  program_id?: number;
  case_episode_id?: number | null;
  title: string;
  description?: string;
  task_type: TaskType;
  status: TaskStatus;
  priority: TaskPriority;
  deadline?: string;
  start_date?: string;
  /** TRUS-697: optional due date AND time (UTC ISO); null clears it. */
  due_at?: string | null;
  /** TRUS-697: the task's own IANA zone (the due time's zone; default the creator's). */
  timezone?: string | null;
  assigned_to?: number;
  created_by?: number;
  completed_at?: string;
  completed_by?: number;
  is_archived: boolean;
  progress_percentage: number;
  tags?: string[];
  custom_fields?: Record<string, any>;
  created_at?: string;
  updated_at?: string;

  // Computed fields
  total_subtasks?: number;
  completed_subtasks?: number;
  is_overdue?: boolean;
  days_until_deadline?: number;

  // Related data
  member_name?: string;
  assigned_to_name?: string;
  created_by_name?: string;
  program_name?: string;

  // Subtasks
  subtasks?: Subtask[];
}

export interface TaskSummary {
  total_tasks: number;
  not_started: number;
  in_progress: number;
  completed: number;
  overdue: number;
  high_priority: number;
}

export interface TaskComment {
  id?: number;
  task_id: number;
  subtask_id?: number;
  comment_text: string;
  created_by?: number;
  created_by_name?: string;
  created_at?: string;
  parent_comment_id?: number;
}

export interface TaskFilters {
  member_id?: number;
  assigned_to?: number;
  status_filter?: TaskStatus;
  priority?: TaskPriority;
  is_archived?: boolean;
  search?: string;
  page?: number;
  page_size?: number;
}

export interface TaskListResponse {
  tasks: Task[];
  total: number;
  page: number;
  page_size: number;
}

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
      'X-CBO-ID': cboId || '',
    }
  };
};

/**
 * Task Management API
 */
export const tasksAPI = {
  /**
   * Get tasks list with optional filters
   */
  getTasks: async (filters?: TaskFilters): Promise<TaskListResponse> => {
    const response = await axios.get(`${API_URL}/api/tasks`, {
      params: filters,
      ...getAuthHeaders()
    });
    return response.data;
  },

  /**
   * Colleagues in the caller's own org that a task can be assigned to.
   * Open to every authenticated org user — unlike `/api/cbos/{id}/users`,
   * which 403s for non-admin roles and left the assignee picker empty.
   */
  getAssignableUsers: async (search?: string): Promise<AssignableUser[]> => {
    const response = await axios.get(`${API_URL}/api/tasks/assignable-users`, {
      params: search ? { search } : undefined,
      ...getAuthHeaders()
    });
    return response.data?.users || [];
  },

  /**
   * Get a specific task by ID with all subtasks
   */
  getTask: async (taskId: number): Promise<Task> => {
    const response = await axios.get(`${API_URL}/api/tasks/${taskId}`, getAuthHeaders());
    return response.data;
  },

  /**
   * Create a new task
   */
  createTask: async (task: Partial<Task>): Promise<Task> => {
    const { offlineAwareMutate } = await import('@/lib/offline/hooks');
    const res: any = await offlineAwareMutate('CREATE', 'tasks', undefined, task, async () => {
      const response = await axios.post(`${API_URL}/api/tasks`, task, getAuthHeaders());
      return response.data;
    });
    return res && res.queued ? ({ id: -Date.now(), ...task } as unknown as Task) : res;
  },

  /**
   * Update an existing task
   */
  updateTask: async (taskId: number, updates: Partial<Task>): Promise<Task> => {
    const { offlineAwareMutate } = await import('@/lib/offline/hooks');
    const res: any = await offlineAwareMutate('UPDATE', 'tasks', String(taskId), updates, async () => {
      const response = await axios.put(`${API_URL}/api/tasks/${taskId}`, updates, getAuthHeaders());
      return response.data;
    });
    return res && res.queued ? ({ id: taskId, ...updates } as unknown as Task) : res;
  },

  /**
   * Delete/archive a task
   */
  deleteTask: async (taskId: number, hardDelete: boolean = false): Promise<void> => {
    await axios.delete(`${API_URL}/api/tasks/${taskId}`, {
      params: { hard_delete: hardDelete },
      ...getAuthHeaders()
    });
  },

  /**
   * Add a subtask to a task
   */
  createSubtask: async (taskId: number, subtask: Partial<Subtask>): Promise<Subtask> => {
    const response = await axios.post(`${API_URL}/api/tasks/${taskId}/subtasks`, subtask, getAuthHeaders());
    return response.data;
  },

  /**
   * Update a subtask
   */
  updateSubtask: async (subtaskId: number, updates: Partial<Subtask>): Promise<Subtask> => {
    const response = await axios.put(`${API_URL}/api/tasks/subtasks/${subtaskId}`, updates, getAuthHeaders());
    return response.data;
  },

  /**
   * Delete a subtask
   */
  deleteSubtask: async (subtaskId: number): Promise<void> => {
    await axios.delete(`${API_URL}/api/tasks/subtasks/${subtaskId}`, getAuthHeaders());
  },

  /**
   * Get task summary for a member
   */
  getMemberTaskSummary: async (memberId: number): Promise<TaskSummary> => {
    const response = await axios.get(`${API_URL}/api/tasks/member/${memberId}/summary`, getAuthHeaders());
    return response.data;
  },

  /**
   * Add a comment to a task
   */
  createComment: async (taskId: number, comment: Partial<TaskComment>): Promise<TaskComment> => {
    const response = await axios.post(`${API_URL}/api/tasks/${taskId}/comments`, comment, getAuthHeaders());
    return response.data;
  },

  /**
   * Get all comments for a task
   */
  getComments: async (taskId: number): Promise<TaskComment[]> => {
    const response = await axios.get(`${API_URL}/api/tasks/${taskId}/comments`, getAuthHeaders());
    return response.data;
  },

  /**
   * Update overdue tasks (admin/system function)
   */
  updateOverdueTasks: async (): Promise<{ updated_count: number }> => {
    const response = await axios.post(`${API_URL}/api/tasks/update-overdue`, {}, getAuthHeaders());
    return response.data;
  },
};

// Status labels for UI
export const STATUS_LABELS: Record<TaskStatus, string> = {
  pending_review: 'Pending Review',
  not_started: 'Not Started',
  in_progress: 'In Progress',
  waiting_on_member: 'Waiting on Member',
  waiting_on_external: 'Waiting on External',
  completed: 'Completed',
  cancelled: 'Cancelled',
  overdue: 'Overdue',
};

// Status colors for badges
export const STATUS_COLORS: Record<TaskStatus, string> = {
  pending_review: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/50 dark:text-yellow-200 dark:border-yellow-700',
  not_started: 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/50 dark:text-blue-200 dark:border-blue-700',
  waiting_on_member: 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/50 dark:text-purple-200 dark:border-purple-700',
  waiting_on_external: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/50 dark:text-orange-200 dark:border-orange-700',
  completed: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/50 dark:text-green-200 dark:border-green-700',
  cancelled: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/50 dark:text-red-200 dark:border-red-700',
  overdue: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/50 dark:text-red-200 dark:border-red-700',
};

// Priority labels
export const PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

// Priority colors
export const PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-200',
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-200',
};

// Task type labels
export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  task: 'Task',
  note: 'Note',
  assessment: 'Assessment',
  follow_up: 'Follow-up',
  documentation: 'Documentation',
  communication: 'Communication',
  care_coordination: 'Care Coordination',
};

export default tasksAPI;
